import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Users, Loader2 } from 'lucide-react';

interface GameData {
  id: string;
  title: string;
  provider: string;
  image: string;
  rating: number;
  category: string;
  description: string;
  game_url?: string;
  date?: string; // Adicionado para eventos esportivos
  time?: string; // Adicionado para eventos esportivos
  oddsHome?: number; // Adicionado para eventos esportivos
  oddsDraw?: number | null; // Adicionado para eventos esportivos
  oddsAway?: number; // Adicionado para eventos esportivos
}

const Sports: React.FC = () => {
  const [sportsEvents, setSportsEvents] = useState<GameData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSportsEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch("https://slots.virtualebank.com.br/games");
        if (!response.ok) {
          throw new Error("Failed to fetch sports events");
        }
        const allGames: GameData[] = await response.json();
        // Filtrar jogos que são da categoria "Esportes"
        const filteredGames = allGames.filter(game => game.category === "Esportes");
        setSportsEvents(filteredGames);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchSportsEvents();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="ml-3 text-lg text-foreground">Carregando eventos esportivos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-center py-16">
        <h1 className="text-3xl font-bold text-red-500 mb-4">Erro ao carregar eventos esportivos</h1>
        <p className="text-muted-foreground">Ocorreu um erro: {error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sports Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 to-green-900 text-white py-20 md:py-32 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              Apostas Esportivas
            </h1>
            <p className="text-lg md:text-xl mb-8 animate-fade-in-up delay-200">
              Aposte nos seus esportes favoritos com as melhores odds e uma variedade de mercados!
            </p>
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-lg px-8 py-6 animate-fade-in-up delay-400" asChild>
              <Link to="/register">Comece a Apostar</Link>
            </Button>
          </div>
        </div>
        <div className="absolute inset-0 z-0 opacity-20">
          <img src="/src/assets/game-sports.jpg" alt="Sports Betting Background" className="w-full h-full object-cover" />
        </div>
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-background via-transparent to-transparent"></div>
      </section>

      {/* Sports Categories Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            Explore Nossos Esportes
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Futebol', 'Basquete', 'Tênis', 'Vôlei', 'MMA', 'eSports'].map((sport, index) => (
              <Card key={index} className="group hover:border-primary/50 transition-all duration-300 hover:shadow-lg cursor-pointer">
                <CardContent className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary">{sport}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Sports Events Section */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            Eventos Esportivos em Destaque
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sportsEvents.map((event) => (
              <Link to={`/game/${event.id}`} key={event.id}>
                <Card className="group relative overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-card cursor-pointer h-full">
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-glow">
                          <Users className="w-7 h-7 text-primary-foreground" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-primary font-medium mb-1">
                        {event.provider} - {event.category}
                      </div>
                      <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                        {event.title}
                      </h3>
                      <div className="flex items-center text-sm text-muted-foreground mt-2">
                        <Calendar className="w-4 h-4 mr-1" /> {event.date}
                        <Clock className="w-4 h-4 ml-4 mr-1" /> {event.time}
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-4">
                        <Button variant="outline" size="sm" className="text-xs font-bold">
                          1 ({event.oddsHome})
                        </Button>
                        {event.oddsDraw && (
                          <Button variant="outline" size="sm" className="text-xs font-bold">
                            X ({event.oddsDraw})
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="text-xs font-bold">
                          2 ({event.oddsAway})
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="border-2 border-primary/50 hover:bg-primary/10 text-lg px-8 py-6" asChild>
              <Link to="/sports">Ver Todos os Eventos Esportivos</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sports;
